import { useEffect, useState, type ComponentType, type ReactNode } from "react";

/**
 * Defers the PostHog provider mount until the browser is idle so the
 * `posthog-js` bundle (~209 KB) doesn't block first paint. Children render
 * immediately wrapped in a passthrough fragment; once `requestIdleCallback`
 * fires (or after 2 s fallback) we dynamic-import `@pontimed/analytics/client`
 * and re-render with the real provider.
 *
 * Trade-off: any events fired in the first ~100 ms after idle are lost. In
 * practice no user can interact that fast, and pageview tracking still runs
 * after mount via the in-app pageview listener which subscribes once the
 * provider is up.
 *
 * When `apiKey` is missing (dev without analytics) we never load the bundle.
 */
interface DeferredPostHogProps {
  apiKey: string | undefined;
  apiHost: string | undefined;
  debug?: boolean;
  appName?: "portal" | "marketing" | "admin";
  environment?: "dev" | "preview" | "prod";
  children: ReactNode;
}

type PostHogProviderModule = typeof import("@pontimed/analytics/client");

export function DeferredPostHog({ apiKey, children, ...config }: DeferredPostHogProps) {
  const [Provider, setProvider] = useState<ComponentType<DeferredPostHogProps> | null>(null);

  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;

    const load = () => {
      void import("@pontimed/analytics/client").then((mod: PostHogProviderModule) => {
        if (cancelled) return;
        setProvider(() => mod.PostHogProvider as unknown as ComponentType<DeferredPostHogProps>);
      });
    };

    if (typeof window === "undefined") return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    let idleHandle: number | undefined;
    let timeoutHandle: number | undefined;
    if (typeof w.requestIdleCallback === "function") {
      idleHandle = w.requestIdleCallback(load);
    } else {
      timeoutHandle = window.setTimeout(load, 2000);
    }

    return () => {
      cancelled = true;
      if (idleHandle !== undefined && typeof w.cancelIdleCallback === "function") {
        w.cancelIdleCallback(idleHandle);
      } else if (timeoutHandle !== undefined) {
        window.clearTimeout(timeoutHandle);
      }
    };
  }, [apiKey]);

  if (!Provider) return <>{children}</>;
  return (
    <Provider apiKey={apiKey} {...config}>
      {children}
    </Provider>
  );
}
